import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (typeof detail === "string" && detail.length > 0) return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return null; // caller can fall back to a more specific message
}

function describeAuthError(error, fallbackAction) {
  // Prefer the backend's `detail` (the actual reason)
  const parsed = formatApiErrorDetail(error?.response?.data?.detail);
  if (parsed) return parsed;

  // No response received = network / CORS / server unreachable
  if (!error?.response) {
    if (error?.code === "ERR_NETWORK") {
      return "Can't reach the server. Please check your internet connection and try again.";
    }
    return `Network error — ${fallbackAction} failed. Please try again in a moment.`;
  }

  // Response received but no useful detail
  const status = error.response.status;
  if (status >= 500) return `Server error (${status}). Please try again shortly.`;
  if (status === 401) return "Invalid email or password.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "Requested resource not found.";
  return error.message || `Unable to complete ${fallbackAction}. Please try again.`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(data);
      } catch (error) {
        setUser(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: describeAuthError(error, "login") };
    }
  };

  const register = async (email, password, name, phone) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name, phone },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: describeAuthError(error, "registration") };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin"
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
